import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { useState } from "react";
import __apexTheater from "@/assets/Apex_Theater.webp.asset.json";
const heroImg = __apexTheater.url;
import apexTheaterRichImg from "@/assets/Apex_Theater.webp.asset.json";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact Apex AV — Free Denver Home Automation & AV Quotes" },
      { name: "description", content: "AV & home theater installers in Denver at 2921 W 38th Ave STE 169. Free in-home consultations Denver Metro. Call (303) 317-3233." },
      { name: "keywords", content: "AV installer near me Denver, home theater installers near me, smart home installer near me Denver, home automation quote Denver, free AV consultation Denver, AV company address Denver 80211, AV company contact Denver Metro" },
      { property: "og:title", content: "Contact Apex AV — Free Denver AV Consultations" },
      { property: "og:description", content: "Call (303) 317-3233 or request a free quote for your Denver home automation or AV project." },
      { property: "og:url", content: "https://apexav.com/contact" },
      { property: "og:type", content: "website" },
      { property: "og:image", content: `https://apexav.com${apexTheaterRichImg.url}` },
      { property: "og:image:alt", content: "Apex AV custom home theater installation in Denver, Colorado" },
      { name: "twitter:title", content: "Contact Apex AV — Free Denver AV Consultations" },
      { name: "twitter:description", content: "Call (303) 317-3233 or request a free quote for your Denver home automation or AV project." },
      { name: "twitter:image", content: `https://apexav.com${apexTheaterRichImg.url}` },
    ],
    links: [{ rel: "canonical", href: "https://apexav.com/contact" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Contact Apex AV",
          url: "https://apexav.com/contact",
          description: "Contact Apex AV for free Denver home automation and AV installation consultations.",
          mainEntity: {
            "@type": ["LocalBusiness", "HomeAndConstructionBusiness"],
            "@id": "https://apexav.com/#organization",
            name: "Apex AV",
            telephone: "+1-303-317-3233",
            email: "info@apexav.com",
            contactPoint: [
              {
                "@type": "ContactPoint",
                telephone: "+1-303-317-3233",
                contactType: "customer service",
                areaServed: "US-CO",
                availableLanguage: ["English"],
              },
            ],
            address: {
              "@type": "PostalAddress",
              streetAddress: "2921 W 38th AVE STE 169",
              addressLocality: "Denver",
              addressRegion: "CO",
              postalCode: "80211",
              addressCountry: "US",
            },
            geo: { "@type": "GeoCoordinates", latitude: 39.7695, longitude: -105.0345 },
            hasMap: "https://www.google.com/maps/search/?api=1&query=2921+W+38th+Ave+STE+169+Denver+CO+80211",
            areaServed: {
              "@type": "Place",
              name: "Denver Metro and Front Range, Colorado",
            },

          },
        }),
      },
    ],
  }),
  component: ContactPage,
});



function ContactPage() {
  const [sent, setSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", message: "" });
  const update = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    const payload = {
      name: form.name.trim(),
      phone: form.phone.trim() || null,
      email: form.email.trim(),
      city: form.city.trim() || null,
      message: form.message.trim(),
    };

    if (!payload.name || !payload.email || !payload.message || !payload.phone) {
      setError("Please complete the required fields.");
      setIsSubmitting(false);
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/.test(payload.email)) {
      setError("Please enter a valid email address (for example name@example.com).");
      setIsSubmitting(false);
      return;
    }

    const response = await fetch("/api/contact", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      setError(
        response.status === 400
          ? "Some details look incorrect — please double-check your email and phone number."
          : "We couldn't send your request. Please call or email us directly.",
      );
      setIsSubmitting(false);
      return;
    }


    setSent(true);
    setForm({ name: "", phone: "", email: "", city: "", message: "" });
    setIsSubmitting(false);
  };

  return (
    <>

    <section className="relative overflow-hidden border-b border-border">
      <div className="absolute inset-0">
        <img src={heroImg} alt="Custom home theater installation by Apex AV in a Cherry Creek Denver luxury home" className="h-full w-full object-cover opacity-30" width={1920} height={1080} loading="eager" fetchPriority="high" decoding="async" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/85 to-background" />
      </div>
      <div className="relative container mx-auto px-6 py-20 md:py-24">
        <div className="max-w-3xl">
          <p className="text-sm font-semibold text-brand-text uppercase tracking-wider">Contact</p>
          <h1 className="mt-3 font-display text-5xl md:text-6xl font-bold">Let's design something great.</h1>
          <p className="mt-6 text-lg text-muted-foreground">
            Free in-home consultations anywhere in the Denver Metro. Tell us about your project — we'll respond within one business day.
          </p>
        </div>
      </div>
    </section>

    <section className="container mx-auto px-6 pb-4">
      <div className="max-w-3xl mx-auto">
        <h2 className="font-display text-3xl md:text-4xl font-bold">What happens after you reach out</h2>
        <p className="mt-4 text-muted-foreground">
          When you call or submit the form, a member of our team follows up within one business day to ask a few questions about your home and project, then schedules a free in-home consultation at a time that works for you. That visit is where we walk the relevant rooms, look at existing wiring and network equipment, and talk through priorities and budget — there's no cost or obligation to move forward.
        </p>
        <h2 className="mt-8 font-display text-3xl md:text-4xl font-bold">What to have ready</h2>
        <p className="mt-4 text-muted-foreground">
          It helps to know which rooms you want addressed first, whether you're mid-remodel or building new (timing matters for prewire), and any must-haves like a specific TV size, existing speakers you want to keep, or a smart home app you already use. If you have floor plans or photos of the spaces, bringing those to the consultation speeds things up, but they're not required.
        </p>
        <h2 className="mt-8 font-display text-3xl md:text-4xl font-bold">Consultation &amp; quoting</h2>
        <p className="mt-4 text-muted-foreground">
          After the walkthrough, we put together a written proposal with equipment, labor, and a project timeline. For larger jobs or new construction, this may include a separate prewire phase scheduled around your builder's timeline. Once you approve the scope, we schedule the install and keep you updated on lead times for any special-order equipment.
        </p>
      </div>
    </section>

    <section className="container mx-auto px-6 py-16">
      <div className="grid gap-12 lg:grid-cols-2 max-w-6xl mx-auto">
        <div>
          <h2 className="font-display text-3xl md:text-4xl font-bold">Get in touch with Apex AV</h2>
          <p className="mt-3 text-muted-foreground">
            Reach a real installer, not a call center. We answer during business hours and return every message the next business day.
          </p>
          <div className="mt-10 space-y-5">
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-lg bg-gradient-brand flex items-center justify-center shrink-0"><Phone className="h-5 w-5 text-brand-foreground" /></div>
              <div><div className="font-semibold">Phone</div><a href="tel:+13033173233" className="text-muted-foreground hover:text-foreground">(303) 317-3233</a></div>
            </div>
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-lg bg-gradient-brand flex items-center justify-center shrink-0"><Mail className="h-5 w-5 text-brand-foreground" /></div>
              <div><div className="font-semibold">Email</div><a href="mailto:info@apexav.com" className="text-muted-foreground hover:text-foreground">info@apexav.com</a></div>
            </div>
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-lg bg-gradient-brand flex items-center justify-center shrink-0"><MapPin className="h-5 w-5 text-brand-foreground" /></div>
              <div>
                <div className="font-semibold">Office & Mailing Address</div>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=2921+W+38th+Ave+STE+169+Denver+CO+80211"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground"
                >
                  2921 W 38th Ave STE 169, Denver, CO 80211
                </a>
                <div className="text-sm text-muted-foreground">Serving Denver Metro &amp; the Front Range</div>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-lg bg-gradient-brand flex items-center justify-center shrink-0"><Clock className="h-5 w-5 text-brand-foreground" /></div>
              <div><div className="font-semibold">Hours</div><div className="text-muted-foreground">Mon–Fri 8a–6p · Sat by appt</div></div>
            </div>
          </div>
        </div>

        <div>
        <h2 className="font-display text-3xl md:text-4xl font-bold">Request a free Denver AV consultation</h2>
        <p className="mt-3 text-muted-foreground">
          Tell us about the home and the rooms you want covered — we'll follow up to schedule a walkthrough at no cost.
        </p>
        <div className="mt-8 rounded-2xl border border-border bg-card p-8 shadow-card">
          {sent ? (
            <div className="text-center py-12">
              <h3 className="font-display text-2xl font-bold">Thanks — we'll be in touch.</h3>

              <p className="mt-3 text-muted-foreground">
                Your consultation request has been sent. We'll respond within one business day.
              </p>
              <button onClick={() => setSent(false)} className="mt-6 text-sm text-muted-foreground hover:text-foreground underline">
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="text-sm font-medium">Name</label>
                  <input id="contact-name" required value={form.name} onChange={update("name")} maxLength={100} className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
                </div>
                <div>
                  <label htmlFor="contact-phone" className="text-sm font-medium">Phone</label>
                  <input id="contact-phone" required type="tel" value={form.phone} onChange={update("phone")} maxLength={30} className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label htmlFor="contact-email" className="text-sm font-medium">Email</label>
                <input id="contact-email" required type="email" value={form.email} onChange={update("email")} maxLength={255} className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
              </div>
              <div>
                <label htmlFor="contact-city" className="text-sm font-medium">City / Suburb</label>
                <input id="contact-city" value={form.city} onChange={update("city")} maxLength={100} className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="e.g. Highlands Ranch" />
              </div>
              <div>
                <label htmlFor="contact-message" className="text-sm font-medium">Tell us about your project</label>
                <textarea id="contact-message" required rows={5} value={form.message} onChange={update("message")} maxLength={2000} className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
              </div>
              <button type="submit" className="w-full rounded-md bg-gradient-brand px-6 py-3 text-sm font-semibold text-brand-foreground shadow-glow hover:opacity-90 transition">
                {isSubmitting ? "Sending..." : "Request Free Consultation"}
              </button>
              {error && <p className="text-sm text-destructive text-center">{error}</p>}
              <p className="text-xs text-muted-foreground text-center">
                Your request is sent securely through our website.
              </p>
            </form>
          )}
        </div>
        </div>
      </div>
    </section>
    <section className="container mx-auto px-6 pb-16">
      <div className="max-w-6xl mx-auto overflow-hidden rounded-2xl border border-border bg-card shadow-card">
        <div className="grid gap-8 md:grid-cols-2">
          <div className="p-8">
            <h2 className="font-display text-3xl md:text-4xl font-bold">Looking for AV installers near&nbsp;Denver?</h2>
            <p className="mt-3 text-muted-foreground">
              {"\n"}
            </p>

            <ul className="mt-6 grid grid-cols-2 gap-2 text-sm text-muted-foreground">
              <li>Denver</li>
              <li>Cherry Creek</li>
              <li>Highlands Ranch</li>
              <li>Littleton</li>
              <li>Centennial</li>
              <li>Parker</li>
              <li>Castle Rock</li>
              <li>Castle Pines</li>
              <li>Aurora</li>
              <li>Lakewood</li>
              <li>Arvada</li>
              <li>Westminster</li>
              <li>Broomfield</li>
              <li>Golden</li>
              <li>Boulder</li>
              <li>And more</li>
            </ul>
            <Link to="/service-areas" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-brand-text hover:gap-3 transition-all">
              See all Denver Metro service areas
            </Link>
          </div>
          <div className="relative min-h-[320px] md:min-h-full bg-muted">
            <iframe
              title="Apex AV office location — 2921 W 38th Ave STE 169, Denver, CO 80211"
              src="https://www.google.com/maps?q=2921+W+38th+Ave+STE+169%2C+Denver%2C+CO+80211&z=14&output=embed"
              className="absolute inset-0 h-full w-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />

          </div>
        </div>
      </div>
    </section>


    </>
  );
}
